import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Copy, Trash2, MoreHorizontal, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SearchImageViewer, SearchImageViewerState } from '@/components/search/SearchImageViewer';
import { FavoriteButton } from './FavoriteButton';
import { Asset } from '@/utils/collectionsGraphQL';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { parseCloudinaryUrl, extractCropFromUrl } from '@/utils/cloudinaryUtils';

// Get original image URL (without transformations) for the viewer
const getOriginalImageUrl = (asset: Asset, type: 'primary' | 'secondary' = 'primary'): string => {
  // First try to get the original URL directly
  const originalUrl = type === 'primary'
    ? (asset.primary_original_url || asset.primary_image)
    : asset.secondary_original_url;

  if (originalUrl) return originalUrl;

  // If no original URL, strip transformations from cropped/markup URL
  const transformedUrl = type === 'primary'
    ? (asset.primary_cropped_url || asset.primary_markup_url)
    : (asset.secondary_cropped_url || asset.secondary_markup_url);

  if (!transformedUrl) return '';

  // Parse and reconstruct without transformations
  const parsed = parseCloudinaryUrl(transformedUrl);
  if (parsed) {
    return `${parsed.baseUrl}/${parsed.assetPath}`;
  }

  return transformedUrl;
};

// Image priority functions for display (tiles)
const getPrimaryDisplayImageUrl = (asset: Asset): string => {
  return asset.primary_cropped_url || asset.primary_original_url || asset.primary_image || '';
};

const getSecondaryDisplayImageUrl = (asset: Asset): string => {
  return asset.secondary_cropped_url || asset.secondary_original_url || '';
};

interface CollectionAssetLightboxProps {
  assetId: string;
  assets: Asset[];
  onClose: () => void;
  onAssetUpdate: () => void;
  onOpenCloudinaryViewer?: (assetId: string) => void;
  onOpenSideBySideEditor?: (assetId: string) => void;
}

export const CollectionAssetLightbox: React.FC<CollectionAssetLightboxProps> = ({
  assetId,
  assets,
  onClose,
  onAssetUpdate,
  onOpenCloudinaryViewer,
  onOpenSideBySideEditor,
}) => {
  const { toast } = useToast();

  // Find current asset
  const [currentAssetId, setCurrentAssetId] = useState(assetId);
  const currentIndex = assets.findIndex((a) => a.id === currentAssetId);
  const currentAsset = assets[currentIndex];

  if (!currentAsset) {
    return null;
  }

  // Viewer state management (per-asset persistence)
  const [viewerStatesMap, setViewerStatesMap] = useState<Map<string, SearchImageViewerState>>(new Map());
  const [comparisonMode, setComparisonMode] = useState<'single' | 'side-by-side'>('single');
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [header, setHeader] = useState(currentAsset.header || '');
  const [subheader, setSubheader] = useState(currentAsset.subheader || '');

  // Dirty state tracking
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Track initial state for dirty comparison
  const initialStateRef = useRef<{
    assetId: string;
    primaryMarker: { x: number; y: number } | null;
    secondaryMarker: { x: number; y: number } | null;
    primaryCrop: string | null;
    secondaryCrop: string | null;
  }>({
    assetId: currentAsset.id,
    primaryMarker: currentAsset.primary_marker_x !== null && currentAsset.primary_marker_y !== null
      ? { x: currentAsset.primary_marker_x, y: currentAsset.primary_marker_y }
      : null,
    secondaryMarker: currentAsset.secondary_marker_x !== null && currentAsset.secondary_marker_y !== null
      ? { x: currentAsset.secondary_marker_x, y: currentAsset.secondary_marker_y }
      : null,
    primaryCrop: currentAsset.primary_cropped_url,
    secondaryCrop: currentAsset.secondary_cropped_url,
  });

  const hasSecondaryImage = !!getSecondaryDisplayImageUrl(currentAsset);
  const companyName = currentAsset.page?.company?.name || currentAsset.slug;

  // Update when asset changes
  useEffect(() => {
    setHeader(currentAsset.header || '');
    setSubheader(currentAsset.subheader || '');
    setIsEditingMetadata(false);
    setIsDirty(false);

    // Reset initial state ref
    initialStateRef.current = {
      assetId: currentAsset.id,
      primaryMarker: currentAsset.primary_marker_x !== null && currentAsset.primary_marker_y !== null
        ? { x: currentAsset.primary_marker_x, y: currentAsset.primary_marker_y }
        : null,
      secondaryMarker: currentAsset.secondary_marker_x !== null && currentAsset.secondary_marker_y !== null
        ? { x: currentAsset.secondary_marker_x, y: currentAsset.secondary_marker_y }
        : null,
      primaryCrop: currentAsset.primary_cropped_url,
      secondaryCrop: currentAsset.secondary_cropped_url,
    };
  }, [currentAsset.id]);

  // Viewer state management
  const getViewerStateKey = (assetId: string, side?: 'primary' | 'secondary') => {
    return side ? `${assetId}-${side}` : assetId;
  };

  const updateViewerState = useCallback(
    (state: SearchImageViewerState, side?: 'primary' | 'secondary') => {
      const key = getViewerStateKey(currentAsset.id, side);
      const previousState = viewerStatesMap.get(key);

      setViewerStatesMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(key, state);
        return newMap;
      });

      // Only mark as dirty if this is an update (not initial state)
      // and if marker position or crop center has actually changed
      if (previousState) {
        const markerChanged =
          Math.abs(previousState.marker.x - state.marker.x) > 1 ||
          Math.abs(previousState.marker.y - state.marker.y) > 1;

        const cropChanged =
          Math.abs(previousState.cropCenter.x - state.cropCenter.x) > 1 ||
          Math.abs(previousState.cropCenter.y - state.cropCenter.y) > 1;

        const zoomChanged = Math.abs(previousState.viewport.zoom - state.viewport.zoom) > 0.01;

        if (markerChanged || cropChanged || zoomChanged) {
          setIsDirty(true);
        }
      }
    },
    [currentAsset.id, viewerStatesMap]
  );

  const getSavedViewerState = (assetId: string, side?: 'primary' | 'secondary') => {
    return viewerStatesMap.get(getViewerStateKey(assetId, side));
  };

  // Extract initial viewport state from saved crop URL
  const getInitialStateFromCropUrl = (cropUrl: string | null) => {
    if (!cropUrl) return {};

    const crop = extractCropFromUrl(cropUrl);
    if (!crop) return {};

    // Calculate the center of the crop area
    const centerX = crop.x + crop.width / 2;
    const centerY = crop.y + crop.height / 2;

    return {
      initialViewportCenterX: centerX,
      initialViewportCenterY: centerY,
    };
  };

  // Navigation handlers with dirty state check
  const attemptNavigation = (navigationFn: () => void) => {
    if (isDirty) {
      setPendingNavigation(() => navigationFn);
      setShowUnsavedDialog(true);
    } else {
      navigationFn();
    }
  };

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentAssetId(assets[currentIndex - 1].id);
    }
  }, [currentIndex, assets]);

  const goToNext = useCallback(() => {
    if (currentIndex < assets.length - 1) {
      setCurrentAssetId(assets[currentIndex + 1].id);
    }
  }, [currentIndex, assets]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setPendingNavigation(() => onClose);
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't handle keyboard shortcuts when editing metadata
      if (isEditingMetadata) return;

      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') attemptNavigation(goToPrev);
      if (e.key === 'ArrowRight') attemptNavigation(goToNext);
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleClose, goToPrev, goToNext, isEditingMetadata, isDirty]);

  // Action handlers
  const handleClone = async () => {
    try {
      const { error } = await supabase.from('app_assets').insert({
        collection_id: currentAsset.collection_id,
        page_id: currentAsset.page_id,
        slug: currentAsset.slug,
        version: currentAsset.version,
        type: currentAsset.type,
        title: `${currentAsset.title} (Copy)`,
        header: currentAsset.header,
        subheader: currentAsset.subheader,
        primary_original_url: currentAsset.primary_original_url,
        primary_cropped_url: currentAsset.primary_cropped_url,
        primary_markup_url: currentAsset.primary_markup_url,
        secondary_original_url: currentAsset.secondary_original_url,
        secondary_cropped_url: currentAsset.secondary_cropped_url,
        secondary_markup_url: currentAsset.secondary_markup_url,
        comparison_mode: currentAsset.comparison_mode,
        is_favorited: false,
      });

      if (error) throw error;

      toast({
        title: 'Asset Cloned',
        description: 'The asset has been duplicated successfully.',
      });

      onAssetUpdate();
    } catch (error) {
      toast({
        title: 'Clone Failed',
        description: error instanceof Error ? error.message : 'Failed to clone asset',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this asset? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase.from('app_assets').delete().eq('id', currentAsset.id);

      if (error) throw error;

      toast({
        title: 'Asset Deleted',
        description: 'The asset has been removed successfully.',
      });

      // Close lightbox or navigate to next asset
      if (assets.length > 1) {
        if (currentIndex < assets.length - 1) {
          goToNext();
        } else if (currentIndex > 0) {
          goToPrev();
        } else {
          onClose();
        }
      } else {
        onClose();
      }

      onAssetUpdate();
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: error instanceof Error ? error.message : 'Failed to delete asset',
        variant: 'destructive',
      });
    }
  };

  const handleSaveMetadata = async () => {
    try {
      const { error } = await supabase
        .from('app_assets')
        .update({
          header: header.trim() || null,
          subheader: subheader.trim() || null,
        })
        .eq('id', currentAsset.id);

      if (error) throw error;

      toast({
        title: 'Metadata Saved',
        description: 'Header and subheader have been updated.',
      });

      setIsEditingMetadata(false);
      onAssetUpdate();
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save metadata',
        variant: 'destructive',
      });
    }
  };

  const handleCancelMetadataEdit = () => {
    setHeader(currentAsset.header || '');
    setSubheader(currentAsset.subheader || '');
    setIsEditingMetadata(false);
  };

  const handleToggleComparison = () => {
    setComparisonMode(comparisonMode === 'single' ? 'side-by-side' : 'single');
  };

  const handleSaveCrop = async (andNavigate: boolean = false) => {
    try {
      // Get the current viewer state(s)
      const singleState = getSavedViewerState(currentAsset.id);
      const primaryState = getSavedViewerState(currentAsset.id, 'primary');
      const secondaryState = getSavedViewerState(currentAsset.id, 'secondary');

      // Determine which URLs and markers to save based on comparison mode
      const updates: any = {};

      if (comparisonMode === 'side-by-side' && hasSecondaryImage) {
        // Primary image
        if (primaryState?.croppedUrl) {
          updates.primary_cropped_url = primaryState.croppedUrl;
        }
        if (primaryState?.croppedAndAnnotatedUrl) {
          updates.primary_markup_url = primaryState.croppedAndAnnotatedUrl;
        }
        if (primaryState?.marker) {
          updates.primary_marker_x = Math.round(primaryState.marker.x);
          updates.primary_marker_y = Math.round(primaryState.marker.y);
        }

        // Secondary image
        if (secondaryState?.croppedUrl) {
          updates.secondary_cropped_url = secondaryState.croppedUrl;
        }
        if (secondaryState?.croppedAndAnnotatedUrl) {
          updates.secondary_markup_url = secondaryState.croppedAndAnnotatedUrl;
        }
        if (secondaryState?.marker) {
          updates.secondary_marker_x = Math.round(secondaryState.marker.x);
          updates.secondary_marker_y = Math.round(secondaryState.marker.y);
        }
      } else {
        // Single view mode
        if (singleState?.croppedUrl) {
          updates.primary_cropped_url = singleState.croppedUrl;
        }
        if (singleState?.croppedAndAnnotatedUrl) {
          updates.primary_markup_url = singleState.croppedAndAnnotatedUrl;
        }
        if (singleState?.marker) {
          updates.primary_marker_x = Math.round(singleState.marker.x);
          updates.primary_marker_y = Math.round(singleState.marker.y);
        }
      }

      if (Object.keys(updates).length === 0) {
        toast({
          title: 'No Changes',
          description: 'No crop changes to save.',
        });
        return;
      }

      const { error } = await supabase
        .from('app_assets')
        .update(updates)
        .eq('id', currentAsset.id);

      if (error) throw error;

      toast({
        title: 'Saved',
        description: 'Crop and marker positions saved.',
      });

      // Clear dirty state
      setIsDirty(false);

      // Update initial state ref
      initialStateRef.current = {
        assetId: currentAsset.id,
        primaryMarker: updates.primary_marker_x !== undefined && updates.primary_marker_y !== undefined
          ? { x: updates.primary_marker_x, y: updates.primary_marker_y }
          : initialStateRef.current.primaryMarker,
        secondaryMarker: updates.secondary_marker_x !== undefined && updates.secondary_marker_y !== undefined
          ? { x: updates.secondary_marker_x, y: updates.secondary_marker_y }
          : initialStateRef.current.secondaryMarker,
        primaryCrop: updates.primary_cropped_url || initialStateRef.current.primaryCrop,
        secondaryCrop: updates.secondary_cropped_url || initialStateRef.current.secondaryCrop,
      };

      onAssetUpdate();

      // Navigate if requested
      if (andNavigate) {
        if (currentIndex < assets.length - 1) {
          goToNext();
        } else {
          onClose();
        }
      }
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save crop',
        variant: 'destructive',
      });
    }
  };

  // Unsaved changes dialog handlers
  const handleDiscardChanges = () => {
    setIsDirty(false);
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleSaveAndContinue = async () => {
    setShowUnsavedDialog(false);
    await handleSaveCrop(true);
    setPendingNavigation(null);
  };

  // Get saved viewer states for current asset (in-memory)
  const savedPrimaryState = getSavedViewerState(currentAsset.id, 'primary');
  const savedSecondaryState = getSavedViewerState(currentAsset.id, 'secondary');
  const savedSingleState = getSavedViewerState(currentAsset.id);

  // Get initial state from database crop URLs (fallback if no in-memory state)
  const primaryInitialState = getInitialStateFromCropUrl(currentAsset.primary_cropped_url);
  const secondaryInitialState = getInitialStateFromCropUrl(currentAsset.secondary_cropped_url);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/95 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-black/50 flex-shrink-0">
          <div className="flex items-center gap-4">
            <h3 className="text-white font-semibold">{companyName}</h3>
            <span className="text-white/60 text-sm">v{currentAsset.version}</span>
            <span className="text-white/40 text-sm">
              {currentIndex + 1} / {assets.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Favorite Button */}
            <FavoriteButton
              assetId={currentAsset.id}
              isFavorited={currentAsset.is_favorited || false}
              variant="icon"
              onToggle={onAssetUpdate}
              className="bg-white/10 hover:bg-white/20 text-white"
            />

            {/* Save Buttons - only show when dirty */}
            {isDirty && (
              <>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => handleSaveCrop(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => handleSaveCrop(true)}
                  className="bg-green-600 hover:bg-green-700 text-white gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save & Next
                </Button>
              </>
            )}

            {/* More Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="text-white hover:bg-white/10">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleClone}>
                  <Copy className="h-4 w-4 mr-2" />
                  Clone Asset
                </DropdownMenuItem>
                {hasSecondaryImage && (
                  <DropdownMenuItem onClick={handleToggleComparison}>
                    {comparisonMode === 'single' ? 'Show Comparison' : 'Single View'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleDelete} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Asset
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="p-2 rounded hover:bg-white/10 text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Main content - Image viewer */}
        <div className="flex-1 flex items-center justify-center relative min-h-0 overflow-hidden">
          {/* Prev button */}
          {currentIndex > 0 && (
            <button
              onClick={() => attemptNavigation(goToPrev)}
              className="absolute left-4 z-30 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}

          {/* Image Viewer(s) */}
          <div className="w-full h-full max-w-[calc(100%-8rem)] mx-16 overflow-hidden">
            {comparisonMode === 'side-by-side' && hasSecondaryImage ? (
              <div className="grid grid-cols-2 gap-4 h-full">
                {/* Primary Image */}
                <div className="h-full overflow-hidden">
                  <SearchImageViewer
                    key={`${currentAsset.id}-primary`}
                    imageUrl={getOriginalImageUrl(currentAsset, 'primary')}
                    initialMarkerX={savedPrimaryState?.marker.x ?? currentAsset.primary_marker_x ?? undefined}
                    initialMarkerY={savedPrimaryState?.marker.y ?? currentAsset.primary_marker_y ?? undefined}
                    initialViewportCenterX={savedPrimaryState?.cropCenter.x ?? primaryInitialState.initialViewportCenterX}
                    initialViewportCenterY={savedPrimaryState?.cropCenter.y ?? primaryInitialState.initialViewportCenterY}
                    initialZoom={savedPrimaryState?.viewport.zoom}
                    initialPanX={savedPrimaryState?.viewport.panX}
                    initialPanY={savedPrimaryState?.viewport.panY}
                    onStateChange={(state) => updateViewerState(state, 'primary')}
                    className="w-full h-full rounded-lg"
                  />
                </div>

                {/* Secondary Image */}
                <div className="h-full overflow-hidden">
                  <SearchImageViewer
                    key={`${currentAsset.id}-secondary`}
                    imageUrl={getOriginalImageUrl(currentAsset, 'secondary')}
                    initialMarkerX={savedSecondaryState?.marker.x ?? currentAsset.secondary_marker_x ?? undefined}
                    initialMarkerY={savedSecondaryState?.marker.y ?? currentAsset.secondary_marker_y ?? undefined}
                    initialViewportCenterX={savedSecondaryState?.cropCenter.x ?? secondaryInitialState.initialViewportCenterX}
                    initialViewportCenterY={savedSecondaryState?.cropCenter.y ?? secondaryInitialState.initialViewportCenterY}
                    initialZoom={savedSecondaryState?.viewport.zoom}
                    initialPanX={savedSecondaryState?.viewport.panX}
                    initialPanY={savedSecondaryState?.viewport.panY}
                    onStateChange={(state) => updateViewerState(state, 'secondary')}
                    className="w-full h-full rounded-lg"
                  />
                </div>
              </div>
            ) : (
              <SearchImageViewer
                key={`${currentAsset.id}-single`}
                imageUrl={getOriginalImageUrl(currentAsset, 'primary')}
                initialMarkerX={savedSingleState?.marker.x ?? currentAsset.primary_marker_x ?? undefined}
                initialMarkerY={savedSingleState?.marker.y ?? currentAsset.primary_marker_y ?? undefined}
                initialViewportCenterX={savedSingleState?.cropCenter.x ?? primaryInitialState.initialViewportCenterX}
                initialViewportCenterY={savedSingleState?.cropCenter.y ?? primaryInitialState.initialViewportCenterY}
                initialZoom={savedSingleState?.viewport.zoom}
                initialPanX={savedSingleState?.viewport.panX}
                initialPanY={savedSingleState?.viewport.panY}
                onStateChange={(state) => updateViewerState(state)}
                className="w-full h-full rounded-lg"
              />
            )}
          </div>

          {/* Next button */}
          {currentIndex < assets.length - 1 && (
            <button
              onClick={() => attemptNavigation(goToNext)}
              className="absolute right-4 z-30 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Footer - Metadata editing */}
        <div className="px-4 py-3 bg-black/50 flex-shrink-0">
          {isEditingMetadata ? (
            <div className="flex gap-2 items-center">
              <Input
                value={header}
                onChange={(e) => setHeader(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveMetadata();
                  if (e.key === 'Escape') handleCancelMetadataEdit();
                }}
                placeholder="Header"
                className="bg-white/10 text-white border-white/20 placeholder:text-white/40"
                autoFocus
              />
              <Input
                value={subheader}
                onChange={(e) => setSubheader(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveMetadata();
                  if (e.key === 'Escape') handleCancelMetadataEdit();
                }}
                placeholder="Subheader"
                className="bg-white/10 text-white border-white/20 placeholder:text-white/40"
              />
              <Button onClick={handleSaveMetadata} size="sm" variant="secondary">
                Save
              </Button>
              <Button onClick={handleCancelMetadataEdit} size="sm" variant="ghost" className="text-white">
                Cancel
              </Button>
            </div>
          ) : (
            <div
              className="text-sm text-white/80 cursor-pointer hover:text-white transition-colors flex items-center gap-2"
              onClick={() => setIsEditingMetadata(true)}
            >
              {header && <span className="font-semibold">{header}</span>}
              {subheader && <span>{subheader}</span>}
              {!header && !subheader && <span className="text-white/40">Click to add header/subheader</span>}
            </div>
          )}
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to the crop or marker position. Would you like to save them before continuing?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDiscardChanges}>
              Discard Changes
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveAndContinue}>
              Save & Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
